/**
 * Drag → PATCH → Undo, as pure functions.
 *
 * ===========================================================================
 * WHY THE DECISION LOGIC IS NOT INSIDE THE DRAG HANDLER
 * ===========================================================================
 * Dropping a card can mean five different things, and only one of them is "send the
 * request": the same stage (a no-op the API also treats as one), a stage with no board
 * column, a move to `lost` that must collect a reason FIRST, an ordinary move, and the
 * reverse of any of those when the operator hits Undo. Deciding that inside a
 * `useDndMonitor` callback would make every branch reachable only by simulating a
 * pointer drag in jsdom — which is exactly the kind of test that gets skipped, in
 * exactly the code path that writes to the audit trail.
 *
 * So the decisions live here, take plain data, and are unit-tested directly
 * (`stage-move.test.ts`). The components do the two things components should do: read
 * the plan and render it.
 *
 * ===========================================================================
 * UNDO IS A SECOND, REAL MUTATION — NOT A ROLLBACK
 * ===========================================================================
 * Part A fixes this and it is not negotiable: "undo = the client issuing the reverse
 * stage PATCH within the toast window. It emits its own event — the audit trail shows
 * both moves, honestly. No server-side undo state."
 *
 * Two consequences the code has to honour:
 *
 *  1. an undo RESETS `stage_entered_at` again, because the server does that on every
 *     transition. A card that was 12 days old, moved, and un-moved reads "0d in stage".
 *     That is not a bug to paper over — the row genuinely changed stage twice, and
 *     faking the original date would make the overdue accent lie about ageing;
 *  2. undoing INTO `lost` needs a reason, because the API requires one for that target
 *     whatever the intent. {@link planUndo} carries the row's ORIGINAL `lost_reason`
 *     for exactly this case, and asks for one when there was none — it never invents a
 *     string like "undo", which would write a fabricated business reason into
 *     `lost_reason` and into the event payload.
 *
 * A ROLLBACK is the different thing that happens when the PATCH FAILS: no request
 * succeeded, so nothing is reversed on the server — the optimistic cache edit is simply
 * discarded by restoring the snapshot taken before it (`useStageMove`). It emits no
 * event, because nothing happened. Conflating the two would put a phantom row in the
 * audit trail for a request the server refused.
 */

import { BOARD_STAGES, STAGES, stageTabLabel } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import type { InquiryDto, StageCountsDto } from '../../lib/inquiries-api';

/** The minimum a caller must know about a row to plan a move. */
export interface MoveSource {
  readonly id: string;
  readonly doc_no: string;
  readonly stage: InquiryStage;
  /** `inquiries.lost_reason` — non-null only while the row IS lost. */
  readonly lost_reason: string | null;
}

export interface StageMove {
  readonly id: string;
  readonly docNo: string;
  readonly from: InquiryStage;
  readonly to: InquiryStage;
  /**
   * The `lost_reason` the row held before this move. Undo needs it: reversing a move
   * OUT of `lost` means moving back INTO `lost`, which the API refuses without one.
   */
  readonly lostReasonBefore: string | null;
  /** The reason attached to THIS move. Non-null only when `to === 'lost'`. */
  readonly reason: string | null;
}

export type MovePlan =
  /** Same stage. The API no-ops too, deliberately, so a re-drop cannot refresh ageing. */
  | { readonly kind: 'noop' }
  /** A drop target that is not one of the five ratified board stages. */
  | { readonly kind: 'refused'; readonly stage: string }
  /** `to === 'lost'` with no reason yet — ask, then re-plan. */
  | { readonly kind: 'reason-required'; readonly move: StageMove }
  /** Send it. `body` is exactly the `ChangeStageDto` the API accepts. */
  | {
      readonly kind: 'patch';
      readonly move: StageMove;
      readonly body: { stage: InquiryStage; reason?: string };
    };

function isBoardStage(value: string): value is InquiryStage {
  return (BOARD_STAGES as readonly string[]).includes(value);
}

/**
 * Plan a drop.
 *
 * `reason` is whatever the operator has supplied so far — `undefined` on the drop
 * itself, a string after the prompt. A blank or whitespace-only string counts as NO
 * reason: the API trims and would answer 422, and accepting `'  '` here would turn a
 * required justification into a formality.
 */
export function planStageMove(
  source: MoveSource,
  target: string,
  reason?: string | null,
): MovePlan {
  if (!isBoardStage(target)) return { kind: 'refused', stage: target };
  if (source.stage === target) return { kind: 'noop' };

  const trimmed = typeof reason === 'string' ? reason.trim() : '';
  const move: StageMove = {
    id: source.id,
    docNo: source.doc_no,
    from: source.stage,
    to: target,
    lostReasonBefore: source.lost_reason,
    reason: target === 'lost' && trimmed !== '' ? trimmed : null,
  };

  if (target === 'lost' && move.reason === null) return { kind: 'reason-required', move };

  return {
    kind: 'patch',
    move,
    body: move.reason === null ? { stage: target } : { stage: target, reason: move.reason },
  };
}

/**
 * Plan the reverse of a completed move.
 *
 * Built from the {@link StageMove} rather than from a re-read row on purpose: by the
 * time Undo is clicked the row's `stage_entered_at` and `lost_reason` have already been
 * rewritten by the first PATCH, so the row no longer knows where it came from. The move
 * does.
 */
export function planUndo(move: StageMove): MovePlan {
  const source: MoveSource = {
    id: move.id,
    doc_no: move.docNo,
    stage: move.to,
    // Reversing back INTO `lost` must carry the reason the row had BEFORE the move —
    // not `move.reason`, which is the reason for the move being undone.
    lost_reason: move.lostReasonBefore,
  };
  return planStageMove(source, move.from, move.lostReasonBefore);
}

/** "Moved to Advance Pending" — `Overlay/Toast` (node 6:665), label from the ONE map. */
export function moveToastText(move: StageMove): string {
  return `Moved to ${stageTabLabel(move.to)}`;
}

/**
 * The confirmation after an undo. Distinct wording, so two toasts are not ambiguous.
 *
 * Takes the UNDO's own move — the one {@link planUndo} produced — and names its DESTINATION.
 * Naming `from` instead would report the column the card was rescued FROM, which is the one
 * place it demonstrably is not.
 */
export function undoToastText(undoMove: StageMove): string {
  return `Moved back to ${stageTabLabel(undoMove.to)}`;
}

/**
 * Why a drop was refused, in words.
 *
 * `follow_up` and `won_closed` are real, reachable enum values with no board column
 * (PHASE2-REPORT §2), so this can only be reached by a malformed drop id — but it says
 * which stage it was rather than "something went wrong".
 */
export function refusalText(stage: string): string {
  const known = Object.values(STAGES).find((descriptor) => descriptor.stage === stage);
  return known === undefined
    ? `'${stage}' is not a stage.`
    : `${known.label} has no board column this phase — move it from the record instead.`;
}

/* ─────────────────────────────────────────────── optimistic cache arithmetic ── */

/**
 * Apply a move to a page of rows.
 *
 * `stage_entered_at` is set to `at` because that is what the server does on every
 * transition, and the days-in-stage chip is computed from it: leaving the old value in
 * place would show "12d in stage" on a card that just moved, then silently correct
 * itself on the next refetch. Two different numbers for the same row is worse than one
 * optimistic number that turns out right.
 */
export function applyMoveToRows(
  rows: readonly InquiryDto[],
  move: StageMove,
  at: string,
): readonly InquiryDto[] {
  return rows.map((row) =>
    row.id === move.id
      ? {
          ...row,
          stage: move.to,
          stage_entered_at: at,
          // The server clears `lost_reason` on any move that is not INTO `lost`.
          lost_reason: move.to === 'lost' ? move.reason : null,
        }
      : row,
  );
}

/**
 * Move one unit between two stage counts.
 *
 * `total` is untouched — a stage change moves a row, it does not create or destroy one,
 * and a `total` that drifted by one on every drag would make the tab strip disagree
 * with the table's own "N inquiries" within a minute of use.
 *
 * Counts are clamped at zero. A negative count is only reachable when the cache was
 * already stale (somebody else moved the row first), and rendering "-1" would turn
 * another user's action into a visible defect in this one's UI.
 */
export function applyMoveToCounts(counts: StageCountsDto, move: StageMove): StageCountsDto {
  const next: Record<InquiryStage, number> = { ...counts.counts };
  next[move.from] = Math.max(0, (next[move.from] ?? 0) - 1);
  next[move.to] = (next[move.to] ?? 0) + 1;
  return { ...counts, counts: next };
}
